<template>
  <div class="sepa">
    <transition
      appear
      enter-active-class="animate__animated animate__fadeIn a3"
    >
      <ui-well
        class="sepa__form"
      >
        <div class="d-flex justify-content-space-between px-6">
          <img class="h-3" :src="'/images/payment/logo-sepa.png'" alt="Sepa logo">
          <p class="font-weight-light">Paiement sécurisé</p>
        </div>
        <p class="text-center text-uppercase my-3 font-weight-bold font-size-130">
          Montant à payer: {{ toString_(price) }}&nbsp;&euro;
        </p>
        <div class="d-flex flex-direction-column">
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="lastName">Nom</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="nom"
                placeholder="Nom"
                :status="UI.STATUS.IDLED"
                name="lastName"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="firstName">Prénom</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="prenom"
                placeholder="Nom"
                :status="UI.STATUS.IDLED"
                name="firstName"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="titulaire_compte">Titulaire du compte</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="titulaire_compte"
                :status="UI.STATUS.IDLED"
                placeholder="Titulaire du compte"
                name="titulaire_compte"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="iban">IBAN</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="iban"
                :status="UI.STATUS.IDLED"
                placeholder="IBAN"
                name="iban"
              />
            </div>
          </div>
          <div class="d-flex justify-content-space-between">
            <p class="font-weight-light">Référence de mandat unique</p>
            <p>{{ mandate }}</p>
          </div>
        </div>
        <p class="text-center font-size-70 font-weight-light text-italic color-gray-medium mt-1 mb-3">
          Vous devez être le titulaire de ce compte, ou disposer des autorisations nécessaires pour agir en son nom.
          Ces coordonnées ne sont pas enregistrées par GHS et ne pourront donc pas être réutilisées pour un prochain paiement.
        </p>
        <ui-form-checkbox
          v-model="isPaymentAuthorized"
          name="confirmPayment"
        >
          J'accepte que mes coordonnées bancaires soient utilisées pour le paiement
          d'un montant de {{ toString_(price) }} € au profit de GHS
        </ui-form-checkbox>
        <layout-cta-super
          component="ui-button"
          class="sepa__button"
          :variant="UI.VARIANT.PRIMARY"
          :status="getPaymentButtonStatus()"
          @click="confirmPayment"
        >
          Confirmer le paiement
        </layout-cta-super>

        <div class="d-flex justify-content-center mt-1">
          <ui-link
            class="sepa__back"
            :route="goBackRoute()"
          >
            Modifier mes informations
          </ui-link>
        </div>
      </ui-well>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/members
@import ~/assets/sass/modules/client/members

.sepa
  height: 90vh // approximate page height accounting for header and footer
  width: 100vw
  background-image: image('/patterns/so-white.png')
  background-repeat: repeat
  position: relative

  .form-group
    margin-bottom: space-x(2)

    &__label
      .ui-label
        font-weight: $font-weight-light

  :deep(.ui-form-checkbox)

    .custom-control-label
      font-size: 80%
      font-weight: $font-weight-light

  &__form
    max-width: 500px
    padding: space-x(6)
    position: absolute
    top: 50%
    right: 50%
    transform: translate(50%,-50%)
    box-shadow: $shadow-discreet
    color: $black

  &__button
    margin-top: auto
    width: 100%
    margin-bottom: space-x(1)
    margin-top: space-x(2)

  &__back
    font-size: 80%
    font-weight: $font-weight-bold
    text-transform: uppercase

    &:hover
      text-decoration: underline
      text-underline-offset: 2px
      text-decoration-color: $primary
</style>

<script lang="ts">
import { mapState } from 'pinia'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { useToastBuilder } from '~/composables/layout/toast'
import Console from '~/utils/lib/core/console'
import { any, isNil, toString } from '~/utils/lib/core/prelude'
import { useStore_hamac_signatures_paiements_sepa } from '~/stores/hamac/signatures/paiements/sepa'
import { toString as toString_ } from '~/utils/lib/core/float'
import { getSignaturesSepaFormRoute, getSignaturesSuccessRoute, getSignaturesRoute } from '~/stores/parametres'
import { DEFAULT_ANONYMOUS_ROUTE, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'

const console = new Console('component:page-parametres-sepa-pay')

function sepaPaymentGateMiddleware_() {
  const navigationContext = useDataOnlyNavigationContext()
  const store = useStore_hamac_signatures_paiements_sepa()
  const sepa = store.$state

  const iban = sepa.iban
  const mandate = sepa.mandate
  const payment_id = sepa.payment_id

  if (!any(isNil)([ iban, payment_id, mandate ]))
    return

  console.warn(
    `missing some information about the payment,`,
    sepa,
    `will be redirected to signatures route`
  )

  store.$reset()

  try {
    const {
      structure: { id },
    } = navigationContext.fromStructureLevel()

    const route = getSignaturesRoute(id)
    return navigateTo(route)
  } catch (err) {
    console.error(
      `no route found, user will be redirected to a safe route`,
      err,
    )
    return navigateTo(DEFAULT_ANONYMOUS_ROUTE)
  }
}

export default defineNuxtComponent({
  setup() {
    const navigationContext = useDataOnlyNavigationContext()

    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      middleware: [
        'session-control-gate',
        'sepa-payment-gate',
        sepaPaymentGateMiddleware_
      ],
    })
    useHead({
      title: `Paiement SEPA — ${navigationContext.fromStructureLevel().structure.raison}`,
    })

    const ToastBuilder = useToastBuilder()

    const store_signature_paiements_sepa = useStore_hamac_signatures_paiements_sepa()

    return {
      ...navigationContext,
      ToastBuilder,
      store_signature_paiements_sepa,
    }
  },
  data() {
    return {
      isPaying: false,
      isPaymentAuthorized: false,
    }
  },
  computed: {
    ...mapState(useStore_hamac_signatures_paiements_sepa, [
      'nom',
      'prenom',
      'titulaire_compte',
      'iban',
      'price',
      'payment_id',
      'mandate'
    ])
  },
  methods: {
    toString_,
    goBackRoute() {
      const {
        structure: { id },
      } = this.fromStructureLevel()

      return getSignaturesSepaFormRoute(id)
    },
    async confirmPayment() {
      try {
        if (!this.isPaying) {
          this.isPaying = true

          const {
            structure: { id, code_client },
          } = this.fromStructureLevel()

          const { paymentData: { _id } } = await this.store_signature_paiements_sepa.validation(
            {
              payment_id: this.payment_id,
              code: code_client,
            }
          )

          const route = getSignaturesSuccessRoute(id, _id)

          this.$router.push(route)
        }
      } catch (err) {
        this.isPaying = false

        console.warn(toString(err), 'confirmPayment')
        return this.ToastBuilder
          .error('Erreur lors du paiement: ' + toString(err))
          .title('Signatures')
          .show()
      }
    },
    getPaymentButtonStatus() {
      if (!this.isPaymentAuthorized) return this.UI.STATUS.DISABLED
      if (this.isPaying) return this.UI.STATUS.DEFFERED
      return this.UI.STATUS.ENABLED
    }
  }
})
</script>
